import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import Image from 'next/image';
import { useRouter } from 'next/router';
import { useTranslation } from 'next-i18next';
import routes from '@utils/routes';
import menuButton from '@assets/images/ic-mobile-menu.svg';
import Link from 'next/link';

import Button, { BUTTON_THEME } from '@components/Button';
import MobileMenu from '@components/Navbar/MobileMenu';
import UserMenu from '@components/Navbar/UserMenu';

import LanguageSelector from '@components/Navbar/LanguageSelector';
import SearchBar from '@components/Autocomplete/SearchBar';

import { useAppContext } from '@context/AppContext';

import FontScaleIcon from '@assets/images/font-scale.svg';
import HighContrastIcon from '@assets/images/high-contrast.svg';
import SearchIcon from '@assets/images/search.svg';
import CloseIcon from '@assets/images/icons/close-white.svg';
import closeButton from '@assets/images/ic-close.svg';
import HighContrastFontScaleIcon from '@assets/images/highcontrast-font.svg';
import HighContrastSearchIcon from '@assets/images/highcontrast-search.svg';
import HighContrastClose from '@assets/images/highcontrast-close.svg';

import {
  PageHeader,
  Content,
  LogoContainer,
  Menu,
  MenuItem,
  Buttons,
  DropdownMenu,
  DropmenuItem,
  Grow,
  IconLink,
  MobileButtonsContainer,
  SearchBarContainer,
  SearchBarContent,
} from '@components/Navbar/styles';

import Logo from '@assets/images/logo-praktyki.png';
import LogoMlodzi from '@assets/images/mlodzi_w_lodzi.png';
import useBreakpoint from '@hooks/useBreakpoint';

type NavbarProps = {
  items: MainNavigation;
};

const HIGHCONTRAST_ENABLED = true;

function Navbar({ items }: NavbarProps) {
  const { matchBreakpoint, BREAKPOINTS } = useBreakpoint();
  const { t } = useTranslation('navbar');
  const { switchFontScale, switchTheme, isAuthenticated, useHighContrastTheme: isHighContrast } = useAppContext();
  const { asPath } = useRouter();

  const [isMobileMenuOpen, setOpenMenuMobile] = useState(false);

  const handlerMenuMobile = useCallback(() => {
    setOpenMenuMobile(!isMobileMenuOpen);
  }, [isMobileMenuOpen]);

  const closeMobileMenu = useCallback(() => {
    setOpenMenuMobile(false);
  }, []);

  const UserSection = useMemo(() => {
    if (!isAuthenticated) {
      return (
        <Button href={routes.signin} buttonTheme={BUTTON_THEME.SECONDARY}>
          {t('Signin')}
        </Button>
      );
    }

    return <UserMenu />;
  }, [isAuthenticated, t]);

  const [searchBar, setSearchBar] = useState(false);
  function useOutsideAlerter(ref: React.RefObject<HTMLDivElement | null>) {
    useEffect(() => {
      function handleClickOutside(event: MouseEvent) {
        const target = event.target;
        if (ref.current && target instanceof Node && !ref.current.contains(target)) {
          if (target instanceof HTMLElement && target.id !== 'showSearchBar') {
            setSearchBar(false);
          }
        }
      }
      // Bind the event listener
      document.addEventListener('mouseup', handleClickOutside);
      return () => {
        // Unbind the event listener on clean up
        document.removeEventListener('mouseup', handleClickOutside);
      };
    }, [ref]);
  }
  const wrapperRef = useRef<HTMLDivElement>(null);
  useOutsideAlerter(wrapperRef);

  return (
    <PageHeader>
      <Content>
        <LogoContainer href={routes.homepage ?? '/'}>
          <Image src={Logo} alt="" fill />
        </LogoContainer>
        <Menu>
          {Object.keys(items).map((key) => {
            const navKey = key as keyof MainNavigation;
            const routeUrl = routes[key as keyof typeof routes] as string;
            return (
              <MenuItem key={key} className="menu-item-link" active={asPath === routeUrl}>
                <Link href={routeUrl} title={t(key)}>
                  {t(key)}
                </Link>
                {items[navKey].length > 0 ? (
                  <DropdownMenu>
                    {items[navKey].map((item: NavigationItem) => (
                      <DropmenuItem key={item.id}>
                        <Link href={item.link} target={item.target} title={item.title}>
                          {item.title}
                        </Link>
                      </DropmenuItem>
                    ))}
                  </DropdownMenu>
                ) : null}
              </MenuItem>
            );
          })}
        </Menu>
        <IconLink
          href="https://mlodziwlodzi.pl/"
          className="navbar-outside-link"
          target="_blank"
          rel="noopener noreferrer"
        >
          <Image src={LogoMlodzi} alt="" />
        </IconLink>
        <Grow />
        <Buttons>
          <button type="button" onClick={switchFontScale}>
            <Image src={(isHighContrast ? HighContrastFontScaleIcon : FontScaleIcon) as string} alt="vis" />
          </button>
          {HIGHCONTRAST_ENABLED ? (
            <button type="button" onClick={switchTheme}>
              <Image src={HighContrastIcon as string} alt="font" />
            </button>
          ) : null}
          <button type="button" className="search-button" onClick={() => setSearchBar(!searchBar)}>
            {searchBar ? (
              <Image
                id="showSearchBar"
                src={(isHighContrast ? HighContrastClose : CloseIcon) as string}
                alt="search"
                width={24}
                height={24}
              />
            ) : (
              <Image
                id="showSearchBar"
                src={(isHighContrast ? HighContrastSearchIcon : SearchIcon) as string}
                alt="search"
                width={40}
                height={40}
              />
            )}
          </button>
        </Buttons>
        <LanguageSelector />

        {UserSection}

        {matchBreakpoint(BREAKPOINTS.MD) ? (
          <>
            <MobileButtonsContainer>
              {isMobileMenuOpen ? (
                <Image src={closeButton as string} alt="" onClick={handlerMenuMobile} />
              ) : (
                <Image src={menuButton as string} alt="" onClick={handlerMenuMobile} />
              )}
            </MobileButtonsContainer>

            <MobileMenu
              items={items}
              openMenuMobile={isMobileMenuOpen}
              handlerMenuMobile={handlerMenuMobile}
              closeMenu={closeMobileMenu}
            />
          </>
        ) : null}
      </Content>
      {searchBar && (
        <SearchBarContainer ref={wrapperRef}>
          <SearchBarContent className="search-bar">
            <SearchBar />
          </SearchBarContent>
        </SearchBarContainer>
      )}
    </PageHeader>
  );
}

export default React.memo(Navbar);
